import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, LogOut, UserCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function ConsentGate({ children }) {
  const [locked, setLocked] = useState(null);
  const [checking, setChecking] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/consent/status").then((r) => setLocked(!!r.data.locked)).catch(() => setLocked(false));
  }, []);

  // Fully end the dancer session (clears cached auth + user state) and return to login.
  const endSession = async () => {
    setLeaving(true);
    try { await logout(); } finally {
      navigate("/login", { replace: true });
    }
  };

  const checkStatus = async () => {
    setChecking(true);
    try {
      const r = await api.get("/consent/status");
      if (r.data.locked) {
        toast.info("Still waiting — a parent or guardian hasn't recorded consent yet.");
      } else {
        toast.success("Consent recorded — you're all set!");
        setLocked(false);
      }
    } catch {
      toast.error("Couldn't check right now. Please try again.");
    }
    setChecking(false);
  };

  if (locked === null) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-teal border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!locked) return children;

  return (
    <div data-testid="consent-locked" className="min-h-screen flex items-center justify-center p-5 bg-background">
      <div className="w-full max-w-md text-center bg-white rounded-2xl border border-gray-100 p-7 sm:p-8 shadow-sm">
        <ShieldCheck className="w-12 h-12 text-teal mx-auto mb-3" />
        <h1 className="font-head text-2xl font-bold">Waiting for parent consent</h1>
        <p className="text-muted-foreground mt-2">Your account is almost ready! A parent or guardian needs to review Studio V's parental notice and give consent before you can see your classes, videos and progress.</p>
        <p className="text-sm text-muted-foreground mt-3">Ask your parent or guardian to sign in and open <b>Parental Consent</b>.</p>

        <div className="mt-6 space-y-2.5">
          <button
            data-testid="consent-check-status-button"
            onClick={checkStatus}
            disabled={checking}
            className="w-full flex items-center justify-center gap-2 bg-teal hover:bg-teal-dark text-white rounded-full py-3.5 font-semibold transition-colors disabled:opacity-60">
            <RefreshCw className={`w-5 h-5 ${checking ? "animate-spin" : ""}`} />{checking ? "Checking…" : "Check Consent Status"}
          </button>
          <button
            data-testid="consent-parent-login-button"
            onClick={endSession}
            disabled={leaving}
            className="w-full flex items-center justify-center gap-2 bg-black hover:bg-gray-800 text-white rounded-full py-3.5 font-semibold transition-colors disabled:opacity-60">
            <UserCheck className="w-5 h-5 text-teal" />Sign In as Parent or Guardian
          </button>
          <button
            data-testid="consent-signout-button"
            onClick={endSession}
            disabled={leaving}
            className="w-full flex items-center justify-center gap-2 bg-white border border-gray-200 hover:border-gray-300 rounded-full py-3.5 font-semibold transition-colors disabled:opacity-60">
            <LogOut className="w-5 h-5" />{leaving ? "Signing out…" : "Sign Out / Back to Login"}
          </button>
        </div>
      </div>
    </div>
  );
}
